import React, { useState, useCallback, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ScrollableTable from "components/ScrollableTable/ScrollableTable";
import { PAGE_ITEMS_COUNT, PLATFORM_LIST, STATUS_LIST, VERSION_PAGE_TABLE_HEADER } from "../../constants";
import { addAppVersion, deleteAppVersion, fetchAppVersionList, updateAppVersion } from "./services";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { NotificationContainer, NotificationManager } from "react-notifications";
import Modal from "components/Modal/Modal.js";
import HistoryIcon from '@material-ui/icons/History';
import Input from "components/Input/Input.js";
import Textarea from "components/Input/Textarea";
import { SelectField } from "components/Input/Select";

const validationSchema = Yup.object().shape({
  version: Yup.string().required("Version is required"),
  platform: Yup.string().required("Platform is required"),
  status: Yup.string().required("Status is required"),
  message: Yup.string().required("Message is required"),
});

const VersionsPage = () => {
  const [appVersionList, setAppVersionList] = useState([]);
  const [appVersion, setAppVersion] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updateData, setUpdateData] = useState(true);
  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getDataArray = () => {
    const dataArray = [];

    appVersionList.forEach((item) => {
      dataArray.push({
        id: item.id,
        columns: [
          item?.version || '',
          getPlatformLabel(item?.platform || ''),
          getStatusLabel(item?.status || ''),
          item?.message || '',
          <div className="user-action" key={item.id}>
            <EditIcon onClick={() => openEditDialog(item)} titleAccess="Edit" />
            <DeleteIcon onClick={() => openDeleteDialog(item)} titleAccess="Delete" />
          </div>,
        ],
      });
    });
  
    return dataArray;
  };

  const getAppVersionList = useCallback(async (start = 0) => {
    setLoading(true);
    const resAppVersion = await fetchAppVersionList({ length: total ? total : PAGE_ITEMS_COUNT, start });
  
    if (resAppVersion?.result) {
      setAppVersionList((Array.isArray(resAppVersion?.data)) ?  resAppVersion.data : [])
      setTotal(resAppVersion?.total || 0)
    } else {
      NotificationManager.error("Failed to load data", "Error", 5000);
    }
    setUpdateData(false);
    setLoading(false);
  }, [total]);

  useEffect(() => {
    getAppVersionList(10);
  }, [getAppVersionList]);

  const openEditDialog = (value = null) => {
    setAppVersion(value);
    setShowCreateEditModal(true);
  }

  const openDeleteDialog = (value) => {
    setAppVersion(value);
    setShowDeleteModal(true);
  }

  const closeModal = () => {
    setShowDeleteModal(false);
    setShowCreateEditModal(false);
    setAppVersion(null);
  }

  const onAppVersionSubmit = async (values) => {
    const api = appVersion ? updateAppVersion : addAppVersion;

    const payload = {
      message:  values?.message || null,
      platform:  values?.platform || null,
      status:  values?.status || null,
      version:  values?.version || null,
    };

    setLoading(true);
    NotificationManager.listNotify = []
    const resData = await api({
        ...(appVersion && {id:  appVersion?.id || null}),
        ...payload,
    });

    if (resData && resData.result) {
      NotificationManager.success("App Version Saved", "Success", 5000);
    } else if (resData.error) {
      NotificationManager.error(resData.error, "Error", 5000);
    }
    setShowCreateEditModal(false);
    setAppVersion(null);
    setUpdateData(true);
    setLoading(false);
  }

  const handleAddItemClick = () => {
    openEditDialog();
  }

  const deleteAppVersionModal = async () => {
    setLoading(true);
    NotificationManager.listNotify = []
    const resData = await deleteAppVersion({ id: appVersion?.id || null });
    if (resData && resData.result) {
      NotificationManager.success("App Version Deleted", "Success", 5000);
    } else if (resData.error) {
      NotificationManager.error(resData.error, "Error", 5000);
    }
    setShowDeleteModal(false);
    setAppVersion(null);
    setUpdateData(true);
    setLoading(false);
  }

  const getStatusLabel = (value) => {
    const item = STATUS_LIST.find(item => item.value === value?.trim());
    return item ? item.label : '';
  };

  const getPlatformLabel = (value) => {
    const item = PLATFORM_LIST.find(item => item.value === value?.trim());
    return item ? item.label : '';
  };

  return (
    <div>
      <ScrollableTable
        minWidth="600px"
        header={VERSION_PAGE_TABLE_HEADER}
        loadData={getAppVersionList}
        loading={loading}
        total={total}
        title="App Version"
        addItemTitle="Add App Version"
        showAddItemDialog={handleAddItemClick}
        updateData={updateData}
        data={getDataArray()}
      />

      {/* Create/Edit Modal */}
      <Modal
        modalClassName="modal-user"
        open={showCreateEditModal}
        handleClose={closeModal}
        title={appVersion ? "Edit App Version" : "Add App Version"}
      >
        {showCreateEditModal && (
          <Formik
            initialValues={{
              version: appVersion?.version || '',
              platform: appVersion?.platform || '',
              status: appVersion?.status || '',
              message: appVersion?.message || '',
            }}
            validationSchema={validationSchema}
            onSubmit={onAppVersionSubmit}
          >
            {({ values, errors, touched }) => (
              <Form>
                <Input
                  error={errors.version}
                  touched={touched.version}
                  icon={<HistoryIcon />}
                  name="version"
                  value={values.version}
                  placeholder="Version"
                  type="text"
                />
                <Field
                  name="platform"
                  className="custom-select-custom"
                  component={SelectField}
                  placeholder="Select Platform"
                  options={PLATFORM_LIST}
                  error={touched.platform ? errors.platform : null}
                  touched={touched.platform}
                />
                <Field
                  name="status"
                  className="custom-select-custom"
                  component={SelectField}
                  placeholder="Select Status"
                  options={STATUS_LIST}
                  error={touched.status ? errors.status : null}
                  touched={touched.status}
                />
                <Textarea
                  name="message"
                  className="custom-select-custom"
                  placeholder="Add Message"
                  touched={touched.message}
                  value={values.message}
                  error={touched.message ? errors.message : null}
                />
                <button type="submit" className="primary-btn-custom">
                  Save App Version
                </button>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal
        modalClassName="modal-user"
        open={showDeleteModal}
        handleClose={closeModal}
        title="Delete App Version"
      >
        <p>Are you sure you want to delete this App Version?</p>

        <button type="button" className="primary-btn-custom" onClick={() => deleteAppVersionModal()}>
          Delete App Version
        </button>
      </Modal>
      <NotificationContainer />
    </div>
  );
};

export default VersionsPage;